/**
 * EvalPanel — a workflow's golden dataset (UI-SPEC §3.5): the eval cases harvested
 * from human-verified runs + seeds + replays. Read-only table; the "Run replay"
 * trigger + add-case actions are injected by the page as client islands in
 * later phases. Composes <DataTable>.
 */

import * as React from "react";
import { DataTable, type DataTableColumn } from "../data-table";
import { IntentBadge } from "../status-badge";

export interface EvalCaseItem {
  id: number;
  input: string;
  reference: string | null;
  source: string;
  labels: string | null;
}

function excerpt(json: string | null, max = 80): string {
  if (!json) return "—";
  try {
    const o = JSON.parse(json);
    const text = o.title ?? o.verdict ?? JSON.stringify(o);
    return String(text).slice(0, max);
  } catch {
    return json.slice(0, max);
  }
}

function sourceIntent(source: string): "success" | "info" | "neutral" {
  if (source === "human_verdict") return "success";
  if (source === "replay") return "info";
  return "neutral";
}

export function EvalPanel({
  cases,
  caseCount,
}: {
  cases: EvalCaseItem[];
  caseCount: number;
}) {
  const columns: DataTableColumn<EvalCaseItem>[] = [
    { key: "id", header: "case", cellClassName: "text-muted-foreground", render: (c) => `#${c.id}` },
    { key: "input", header: "input", render: (c) => excerpt(c.input) },
    {
      key: "reference",
      header: "reference",
      cellClassName: "text-muted-foreground",
      render: (c) => excerpt(c.reference, 40),
    },
    {
      key: "source",
      header: "source",
      align: "center",
      render: (c) => <IntentBadge intent={sourceIntent(c.source)}>{c.source.replace("_", " ")}</IntentBadge>,
    },
    {
      key: "labels",
      header: "labels",
      cellClassName: "type-subtext text-muted-foreground",
      render: (c) => {
        if (!c.labels) return "—";
        try {
          return (JSON.parse(c.labels) as string[]).join(", ");
        } catch {
          return c.labels;
        }
      },
    },
  ];
  return (
    <DataTable
      rows={cases}
      columns={columns}
      rowKey={(c) => c.id}
      title="Golden dataset"
      count={caseCount}
      subtitle="Harvested from human-verified runs, seeds, and replays."
      empty="No eval cases yet — harvest from verified runs."
    />
  );
}
